import React, { useState, useMemo } from 'react';
import { Sparkles, Copy, Check, Loader2, FileCode, RefreshCw } from 'lucide-react';

const AiMetaGenerator = ({ project, apiFetch, showNotification }) => {
  const pages = useMemo(() => project?.technical_audit?.page_summaries || [], [project]);
  const [selectedUrl, setSelectedUrl] = useState(pages[0]?.url || '');
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  const current = pages.find((p) => p.url === selectedUrl) || pages[0];

  const handleGenerate = async () => {
    if (!current) return;
    setLoading(true);
    setResult(null);
    try {
      const payload = {
        url: current.url,
        keyword: keyword || (current.serp_preview?.raw_title || '').split('|')[0].trim(),
        page_info: {
          title: current.page_info?.title,
          meta_description: current.page_info?.meta_description,
          h1_content: current.headings?.h1_content,
          word_count: current.content?.word_count,
        },
      };
      const res = await apiFetch('/seo/generate-meta', { method: 'POST', body: JSON.stringify(payload) });
      setResult(res);
      showNotification('Optimized meta tags generated!', 'success');
    } catch (err) {
      showNotification('Could not generate meta tags: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  if (!pages.length) return null;

  return (
    <div className="bg-gradient-to-br from-purple-950/20 to-black/40 border border-purple-500/25 rounded-2xl p-5 flex flex-col gap-4 animate-in fade-in duration-300">
      <div className="flex items-center gap-2">
        <Sparkles size={16} className="text-purple-400" />
        <h4 className="font-extrabold text-white text-sm">Fix with AI — Auto-Generated Meta Tags & Titles</h4>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <select
          value={selectedUrl}
          onChange={(e) => { setSelectedUrl(e.target.value); setResult(null); }}
          className="bg-black/60 border border-gray-700 text-blue-300 font-mono text-xs rounded-lg px-3 py-2 focus:outline-none focus:border-purple-500 max-w-full truncate cursor-pointer font-semibold shadow-inner"
        >
          {pages.map((p, idx) => (
            <option key={idx} value={p.url}>📄 {p.url}</option>
          ))}
        </select>

        <input
          type="text"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="Target keyword (optional — inferred from title)"
          className="bg-black/60 border border-gray-700 text-gray-200 text-xs rounded-lg px-3 py-2 focus:outline-none focus:border-purple-500 placeholder-gray-600 font-sans shadow-inner"
        />

        <button
          onClick={handleGenerate}
          disabled={loading}
          className="bg-purple-900/50 hover:bg-purple-900/70 border border-purple-500/30 text-purple-200 font-bold px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-all text-xs disabled:opacity-50"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {loading ? 'Generating...' : 'Fix with AI'}
        </button>
      </div>

      {loading && (
        <div className="bg-black/50 p-6 rounded-lg border border-purple-500/20 text-center animate-pulse text-purple-300 font-mono text-[11px]">
          ⚙️ Querying LLM engine for keyword-optimized metadata...
        </div>
      )}

      {result && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 animate-in fade-in duration-300">
          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-center">
              <span className="text-[9px] text-green-400 font-bold uppercase tracking-wide flex items-center gap-1">
                <FileCode size={11} /> Optimized Title ({result.title?.length || 0} chars)
              </span>
              <button
                onClick={() => handleCopy(result.title, 'title')}
                className="text-[9px] text-gray-400 hover:text-white flex items-center gap-1 bg-gray-850 px-2 py-0.5 rounded border border-gray-800 transition"
              >
                {copiedField === 'title' ? <Check size={10} className="text-green-500" /> : <Copy size={10} />}
                {copiedField === 'title' ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <div className="bg-gray-950 border border-green-500/20 rounded-xl p-3 font-sans text-sm text-green-200 leading-snug">
              &lt;title&gt;{result.title}&lt;/title&gt;
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-center">
              <span className="text-[9px] text-green-400 font-bold uppercase tracking-wide flex items-center gap-1">
                <FileCode size={11} /> Optimized Meta Description ({result.meta_description?.length || 0} chars)
              </span>
              <button
                onClick={() => handleCopy(result.meta_description, 'desc')}
                className="text-[9px] text-gray-400 hover:text-white flex items-center gap-1 bg-gray-850 px-2 py-0.5 rounded border border-gray-800 transition"
              >
                {copiedField === 'desc' ? <Check size={10} className="text-green-500" /> : <Copy size={10} />}
                {copiedField === 'desc' ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <div className="bg-gray-950 border border-green-500/20 rounded-xl p-3 font-sans text-xs text-green-200 leading-snug whitespace-pre-wrap">
              &lt;meta name="description" content="{result.meta_description}" /&gt;
            </div>
          </div>

          {result.explanation && (
            <div className="lg:col-span-2 text-xs text-gray-400 italic bg-purple-950/15 border border-purple-500/10 p-2.5 rounded-lg">
              {result.explanation}
            </div>
          )}

          <button
            onClick={handleGenerate}
            className="lg:col-span-2 text-[10px] text-purple-300 hover:text-purple-200 flex items-center gap-1.5 justify-center bg-purple-950/30 border border-purple-500/20 rounded-lg py-2 transition"
          >
            <RefreshCw size={11} /> Regenerate
          </button>
        </div>
      )}
    </div>
  );
};

export default AiMetaGenerator;
